// @ts-nocheck
import { create } from 'zustand';
import type { Dealership, Playlist, PlaylistGroup } from '../../api/types';
import type { ResourceResponse } from '../../api/types/response';
import { apiGet, apiPatch } from '../../js/api';
import api from '../../api';
import {
    REQUESTS,
    RESPONSE,
    DEFAULT_PLAYLIST_SORT_BY
} from '../../js/_constants';
import Server from '../../js/config/_server';

export interface PlaylistWithExpandedDealerships
    extends Omit<Playlist, 'dealerships'> {
    dealerships: Dealership[];
}

type SelectedPlaylist = {
    loading: boolean;
} & Partial<Playlist>;

type PlaylistsState = {
    loading: boolean;
    items: PlaylistWithExpandedDealerships[];
};

type PlaylistRequestConfig = {
    sortBy?: string;
    isBackgroundRequest?: boolean;
};

type PlaylistGroupRequestConfig = {
    isBackgroundRequest?: boolean;
};

type StoreState = {
    // state
    selectedPlaylist: SelectedPlaylist;
    selectedPlaylistGroup: SelectedPlaylist;
    playlists: PlaylistsState;
    playlistId: string;
    playlistGroupId: string;
    playlistSortBy: string;

    // actions
    getPlaylistsRequest: (config?: PlaylistRequestConfig) => void;
    getPlaylists: (sortBy?: string) => Promise<void>;
    getPlaylist: (
        uuid: string,
        config?: PlaylistRequestConfig
    ) => Promise<void>;
    getPlaylistGroup: (
        uuid: string,
        config?: PlaylistGroupRequestConfig
    ) => Promise<void>;

    setPlaylistId: (id: string) => void;
    setPlaylistGroupId: (id: string) => void;

    setSelectedPlaylist: (playlist: Partial<Playlist>) => void;
    updatePlaylist: (
        playlist: Partial<Record<keyof Playlist, string | string[]>>
    ) => Promise<void>;
    updatePlaylistGroup: (
        playlistGroup: Partial<Record<keyof Playlist, string | string[]>>
    ) => Promise<void>;
};

const defaultConfig = {
    sortBy: DEFAULT_PLAYLIST_SORT_BY,
    isBackgroundRequest: false
};

const usePlaylistsStore = create<StoreState>((set, get) => ({
    // ------------------
    // initial state
    // ------------------
    selectedPlaylist: {
        loading: true
    },
    selectedPlaylistGroup: {
        loading: true
    },
    playlists: {
        loading: true,
        items: []
    },
    playlistId: '',
    playlistGroupId: '',
    playlistSortBy: DEFAULT_PLAYLIST_SORT_BY,

    // ------------------
    // playlists
    // ------------------
    getPlaylistsRequest: (config = defaultConfig) => {
        const { sortBy, isBackgroundRequest } = config;

        set(state => ({
            playlists: {
                ...state.playlists,
                loading: isBackgroundRequest || true
            },
            playlistSortBy: sortBy || state.playlistSortBy
        }));

        get().getPlaylists(sortBy);
    },

    getPlaylists: async (sortBy) => {
        try {
            const response = await api.getPlaylists(
                sortBy || DEFAULT_PLAYLIST_SORT_BY
            );

            set({
                playlists: {
                    items: response[RESPONSE.EXPAND_COLLECTION],
                    loading: false
                }
            });
        } catch (error) {
            throw new Error(`There was an error ${error}`);
        }
    },

    // ------------------
    // single playlist
    // ------------------
    getPlaylist: async (uuid, config = defaultConfig) => {
        const url = `${Server.baseUrl}${Server.services.playlists.get}/${uuid}`;

        try {
            if (!config.isBackgroundRequest) {
                set(state => ({
                    selectedPlaylist: {
                        ...state.selectedPlaylist,
                        loading: true
                    }
                }));
            }

            const response = await apiGet<ResourceResponse<Playlist>>(url, {
                accept: REQUESTS.ACCEPT.JSON_LD
            });

            // expand items
            const items = await apiGet<ResourceResponse<Playlist>>(url + '/items', {
                accept: REQUESTS.ACCEPT.JSON_LD
            });

            response.items = items[RESPONSE.EXPAND_COLLECTION];

            set({
                selectedPlaylist: {
                    loading: false,
                    ...response
                }
            });
        } catch (error) {
            throw new Error(`There was an error ${error}`);
        }
    },

    // ------------------
    // playlist group
    // ------------------
    getPlaylistGroup: async (uuid, config = defaultConfig) => {
        const url = `${Server.baseUrl}${Server.services.playlistGroups.get}/${uuid}`;

        try {
            if (!config.isBackgroundRequest) {
                set(state => ({
                    selectedPlaylistGroup: {
                        ...state.selectedPlaylistGroup,
                        loading: true
                    }
                }));
            }

            const response = await apiGet<ResourceResponse<PlaylistGroup>>(url, {
                accept: REQUESTS.ACCEPT.JSON_LD
            });

            const playlistGroupItems = await apiGet<ResourceResponse<PlaylistGroup>>(url + '/items', {
                accept: REQUESTS.ACCEPT.JSON_LD
            })

            const playlists = await Promise.all(
                playlistGroupItems[RESPONSE.EXPAND_COLLECTION].map(item =>
                    apiGet(Server.baseUrl + item.playlist, {
                        accept: REQUESTS.ACCEPT.JSON_LD
                    })
                )
            );

            playlists.forEach((p, i) => {
                p.itemId = playlistGroupItems[RESPONSE.EXPAND_COLLECTION][i][RESPONSE.ID];
                p.position = playlistGroupItems[RESPONSE.EXPAND_COLLECTION][i].position;
                p.schedules = playlistGroupItems[RESPONSE.EXPAND_COLLECTION][i].schedules;
            });

            response.items = playlists;

            set({
                selectedPlaylistGroup: {
                    loading: false,
                    ...response
                }
            });
        } catch (error) {
            throw new Error(`There was an error ${error}`);
        }
    },

    // ------------------
    // setters
    // ------------------
    setPlaylistId: playlistId => set({ playlistId }),
    setPlaylistGroupId: playlistGroupId => set({ playlistGroupId }),

    setSelectedPlaylist: updatedPlaylist =>
        set(state => ({
            selectedPlaylist: {
                ...state.selectedPlaylist,
                ...updatedPlaylist
            }
        })),

    // ------------------
    // updates
    // ------------------
    updatePlaylist: async (updatedPlaylist) => {
        const { selectedPlaylist } = get();
        const url = `${Server.baseUrl}${selectedPlaylist[RESPONSE.ID]}`;

        const next = await apiPatch(url, {
            body: { ...updatedPlaylist }
        });

        set({
            selectedPlaylist: next
        });
    },

    updatePlaylistGroup: async (updatedPlaylistGroup) => {
        const { selectedPlaylistGroup } = get();
        const url = `${Server.baseUrl}${selectedPlaylistGroup[RESPONSE.ID]}`;

        const next = await apiPatch(url, {
            body: { ...updatedPlaylistGroup }
        });

        set({
            selectedPlaylistGroup: next
        });
    }
}));

export default usePlaylistsStore;